/* @HEADER
 * @version 1.1.899 | 2026-10-25
 * @purpose What a person sees while a link has not opened: that it is being opened, that there is no connection, that it is not available or that it has not reached this device yet; never an empty screen and never a name.
 * @sidecar NavLinkNotice.tsx.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8278, ZVX-DEV-7134

/**
 * What a person sees while a link has not opened: that it is being opened, that there is no connection, that it is not
 * available, or that it has not reached this device yet (ZVX-4464). Never an empty screen, and never a name: the words carry
 * nothing about what the link pointed at. Pure view; `useNavLinkOpen` decides which one and what the buttons do.
 */

import type { ReactElement } from 'react'
import { useLocale } from '../shared/i18n/MessagesContext'
import { resolveNavLinkWords } from '../shared/i18n/navLinkWords'
import { NAV_LINK_TESTIDS } from './navLink.testids'

export type NavLinkNoticeKind = 'waiting' | 'no-connection' | 'unavailable' | 'not-here-yet'

export interface NavLinkNoticeProps {
  notice: NavLinkNoticeKind | null
  onRetry: () => void
  onToProjects: () => void
}

export function NavLinkNotice({ notice, onRetry, onToProjects }: NavLinkNoticeProps): ReactElement | null {
  const { locale } = useLocale()
  if (notice === null) return null
  const w = resolveNavLinkWords(locale)
  const [title, body] =
    notice === 'waiting'
      ? [w.waitingTitle, w.waitingBody]
      : notice === 'no-connection'
        ? [w.noConnectionTitle, w.noConnectionBody]
        : notice === 'not-here-yet'
          ? [w.notHereYetTitle, w.notHereYetBody]
          : [w.unavailableTitle, w.unavailableBody]
  return (
    <div
      role={notice === 'waiting' ? 'status' : 'alert'}
      data-testid={NAV_LINK_TESTIDS.notice}
      data-notice={notice}
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        maxWidth: 'min(480px, calc(100vw - 32px))',
        padding: '12px 16px',
        borderRadius: 8,
        background: 'var(--surface, #fff)',
        color: 'var(--text, #111)',
        border: '1px solid var(--border, #ccc)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      }}
    >
      <strong>{title}</strong>
      <p style={{ margin: '4px 0 8px' }}>{body}</p>
      {notice === 'no-connection' || notice === 'not-here-yet' ? (
        <button type="button" data-testid={NAV_LINK_TESTIDS.retry} onClick={onRetry}>
          {w.retry}
        </button>
      ) : null}
      {notice === 'unavailable' ? (
        <button type="button" data-testid={NAV_LINK_TESTIDS.toProjects} onClick={onToProjects}>
          {w.toProjects}
        </button>
      ) : null}
    </div>
  )
}
